import sortBy from 'lodash/sortBy'
import { Clazz, IGradeClazzSelectOption, Option, ISportsTeam, IGradeClazzJoin } from 'kuaidong-model'
import { AClazz, ASchool, ATeacher } from 'kuaidong-api'
import groupBy from 'lodash/groupBy'
import { loginStore } from 'kuaidong-store'
import { SchoolUtil } from 'kuaidong-lib'
import { Form, Register } from './FormService'

export const CLASS_PER_GRADE = 500

type OptionCbOfClazz = (clazz: Clazz) => { label: string; value: any; disabled?: boolean; meta?: any }

const DEFAULT_GRADES: Option<number>[] = [
  { value: 7, label: '小班' },
  { value: 8, label: '中班' },
  { value: 9, label: '大班' },
  { value: 10, label: '入学前' },
  { value: 11, label: '一年级' },
  { value: 12, label: '二年级' },
  { value: 13, label: '三年级' },
  { value: 14, label: '四年级' },
  { value: 15, label: '五年级' },
  { value: 16, label: '六年级' },
  { value: 17, label: '七年级' },
  { value: 18, label: '八年级' },
  { value: 19, label: '九年级' },
  { value: 31, label: '高一' },
  { value: 32, label: '高二' },
  { value: 33, label: '高三' },
  { value: 61, label: '大一' },
  { value: 62, label: '大二' },
  { value: 63, label: '大三' },
  { value: 64, label: '大四' },
  { value: 71, label: '研一' },
  { value: 72, label: '研二' },
  { value: 73, label: '研三' },
  { value: 74, label: '研四' },
  { value: 80, label: '博士' },
].map((grade) => ({ value: grade.value, label: SchoolUtil.buildGradeName(grade.value) }))

export class ClassService {
  static ALL_GRADES = -2
  static ALL_CLASSES = -3
  static CLASS_PER_GRADE = CLASS_PER_GRADE

  static formatClass(classNo: number) {
    if (classNo === ClassService.ALL_CLASSES) {
      return '所有班级'
    } else if (classNo > 0) {
      return `${classNo}班`
    } else {
      return ''
    }
  }

  static formatGrade(gradeNo: number): string {
    if (gradeNo === ClassService.ALL_GRADES) {
      return '所有年级'
    }

    const gradeInZHCN = ['一', '二', '三', '四', '五', '六', '七', '八', '九']

    if (gradeNo === 7) {
      return '小班'
    } else if (gradeNo === 8) {
      return '中班'
    } else if (gradeNo === 9) {
      return '大班'
    } else if (gradeNo === 10) {
      return '入学前'
    } else if (gradeNo >= 31 && gradeNo <= 33) {
      return `高${gradeInZHCN[gradeNo - 31]}`
    } else if (gradeNo >= 61 && gradeNo <= 64) {
      return `大${gradeInZHCN[gradeNo - 61]}`
    } else if (gradeNo >= 11 && gradeNo <= 19) {
      return `${gradeInZHCN[gradeNo - 11]}年级`
    } else if (gradeNo >= 71 && gradeNo <= 74) {
      return `研${gradeInZHCN[gradeNo - 71]}`
    } else if (gradeNo === 80) {
      return '博士'
    }

    return '其他年级'
  }

  // 体质测试专用
  static formatGradeOfExam(gradeNo: number): string {
    const gradeInZHCN = ['一', '二', '三', '四', '五', '六', '七', '八', '九']

    if (gradeNo >= 31 && gradeNo <= 33) {
      return `高${gradeInZHCN[gradeNo - 31]}`
    } else if (gradeNo >= 41 && gradeNo <= 44) {
      return `大${gradeInZHCN[gradeNo - 41]}`
    } else if (gradeNo >= 11 && gradeNo <= 16) {
      return `${gradeInZHCN[gradeNo - 11]}年级`
    } else if (gradeNo >= 21 && gradeNo <= 23) {
      return `${gradeInZHCN[gradeNo - 15]}年级`
    } else if (gradeNo >= 51 && gradeNo <= 54) {
      return `研${gradeInZHCN[gradeNo - 51]}`
    } else if (gradeNo === 60) {
      return '博士'
    }

    return '其他年级'
  }

  static formatClassWithGrade(gradeNo: number, classNo: number) {
    const formattedGrade = this.formatGrade(gradeNo)
    if (!formattedGrade) {
      return ''
    }
    return `${this.formatGrade(gradeNo)}${this.formatClass(classNo)}`
  }

  static buildGradeOptions(clazzes: Clazz[]) {
    return Array.from(new Set(clazzes.map((clazz) => clazz.grade_no)))
      .sort((a, b) => a - b)
      .map((grade) => ({
        label: this.formatGrade(grade),
        value: grade,
      }))
  }

  static buildClassOptionWithGradeNoClazzNo(clases: Clazz[]) {
    const res: IGradeClazzSelectOption[] = []
    const groupByGradeNo = groupBy(clases, 'grade_no')

    for (const grade_no in groupByGradeNo) {
      const curGrade: IGradeClazzSelectOption = {
        label: SchoolUtil.buildGradeName(Number(grade_no)) || '其他年级',
        value: Number(grade_no),
        children: groupByGradeNo[grade_no].map((clz) => {
          return {
            label: clz.name,
            value: clz.class_no,
          }
        }),
      }
      res.push(curGrade)
    }
    return res
  }

  static buildClassOptions(classes: Clazz[], allGrades = false, allClasses = false, standardClazzName = false) {
    const sorted = sortBy(classes, ['grade_no', 'class_no'])
    const result: Option<number>[] = []

    if (allGrades) {
      result.push({
        value: this.ALL_GRADES,
        label: this.formatGrade(this.ALL_GRADES),
        children: [
          {
            value: this.ALL_CLASSES,
            label: this.formatClass(this.ALL_CLASSES),
          },
        ],
      })
    }

    function lastGradeNo(gradeId: number) {
      const clazz = classes.find((c) => c.grade_id === gradeId)
      if (clazz) {
        return clazz.grade_no
      }
    }

    sorted.forEach((clazz) => {
      const last = result.length ? result[result.length - 1] : null

      if (last && lastGradeNo(last.value) === clazz.grade_no) {
        last.children!.push({
          value: clazz.id,
          label: clazz.note_name || (standardClazzName ? clazz.name : this.formatClass(clazz.class_no)),
        })
      } else {
        result.push({
          value: clazz.grade_id,
          label: this.formatGrade(clazz.grade_no),
          children: [
            ...(allClasses
              ? [
                  {
                    value: this.ALL_CLASSES,
                    label: this.formatClass(this.ALL_CLASSES),
                  },
                ]
              : []),
            {
              value: clazz.id,
              label: clazz.note_name
                ? clazz.note_name
                : standardClazzName
                ? clazz.name
                : this.formatClass(clazz.class_no),
            },
          ],
        })
      }
    })

    return result
  }

  static buildDefaultClassOptionsOld(classesJoinMap?: Record<number, number>) {
    // 排除入学前
    return DEFAULT_GRADES.filter((x) => x.value !== 10).map((grade) => ({
      ...grade,
      children: Array.from(
        Array(
          !classesJoinMap?.[grade.value] || classesJoinMap[grade.value] === -1
            ? CLASS_PER_GRADE
            : classesJoinMap[grade.value]
        ).keys()
      ).map((x) => {
        return { value: x + 1, label: `${x + 1}班` }
      }),
    }))
  }

  static async buildDefaultClassOptionsWithNoteName() {
    // console.log('buildDefaultClassOptionsWithNoteName')
    const schoolClazzInfos = await AClazz.clazzesOptions()
    return schoolClazzInfos.map((x) => this.buildClassOptionsByGradeClazzJoinInfo(x))
    /*
    let result = []
    // 是否是合作校
    if (schoolClazzInfo.type === 'class_limited') {
      // 合作校根据返回列表构建选项
      const groupByGradeNo = groupBy(schoolClazzInfo.classes, 'grade_no')
      // console.log(groupByGradeNo)
      result = DEFAULT_GRADES.filter((item) => Object.keys(groupByGradeNo).includes(String(item.value))).map(
        (grade) => {
          return {
            ...grade,
            children: groupByGradeNo[grade.value].map((clazz) => ({
              label: clazz.note_name !== null ? `${clazz.class_no}班(${clazz.note_name})` : `${clazz.class_no}班`,
              value: clazz.class_no,
            })),
          }
        }
      )
    } else if (schoolClazzInfo.type === 'grade_limited') {
      // 过期合作校根据返回列表构建选项，不限制班级
      const groupByGradeNo = groupBy(schoolClazzInfo.classes, 'grade_no')
      result = DEFAULT_GRADES.filter((item) => Object.keys(groupByGradeNo).includes(String(item.value))).map(
        (grade) => {
          return {
            ...grade,
            children: Array.from(Array(CLASS_PER_GRADE).keys()).map((x) => {
              const curGradeNo = grade.value
              const curClazzNo = x + 1
              let noteName = null
              if (groupByGradeNo[curGradeNo] !== undefined) {
                const clazzInfo = groupByGradeNo[curGradeNo].find((clazz) => {
                  return clazz.class_no === curClazzNo
                })
                if (clazzInfo) {
                  noteName = clazzInfo.note_name
                }
              }

              return {
                value: curClazzNo,
                label: noteName !== null ? `${curClazzNo}班(${noteName})` : `${curClazzNo}班`,
              }
            }),
          }
        }
      )
    } else {
      // 非合作校，构建全部选项
      let schoolAllClassInfo = await AClazz.all()
      schoolAllClassInfo = schoolAllClassInfo.filter((clazz) => {
        return clazz.note_name !== null
      })
      const groupByGradeNo = groupBy(schoolAllClassInfo, 'grade_no')
      result = DEFAULT_GRADES.map((grade) => ({
        ...grade,
        children: Array.from(Array(CLASS_PER_GRADE).keys()).map((x) => {
          const curGradeNo = grade.value
          const curClazzNo = x + 1
          let noteName = null
          if (groupByGradeNo[curGradeNo] !== undefined) {
            const clazzInfo = groupByGradeNo[curGradeNo].find((clazz) => {
              return clazz.class_no === curClazzNo
            })
            if (clazzInfo) {
              noteName = clazzInfo.note_name
            }
          }

          return { value: curClazzNo, label: noteName !== null ? `${curClazzNo}班(${noteName})` : `${curClazzNo}班` }
        }),
      }))
    }

    // 构建暑假班级选项
    const targetGrades = await AClazz.getSchoolGrades()
    const existsGrade = result.map((item) => item.value)
    const summerGrades = targetGrades.filter((item) => !existsGrade.includes(item))
    if (summerGrades.length > 0) {
      result = [this.buildGradeFullClassOptions(summerGrades[0]), ...result]
    }
    return result
    */
  }

  // 构建默认的班级选项
  static buildGradeFullClassOptions(gradeNo: number) {
    return {
      label: SchoolUtil.buildGradeName(gradeNo, { disabledSummerName: true }),
      value: gradeNo,
      children: Array.from(Array(CLASS_PER_GRADE).keys()).map((x) => {
        const curClazzNo = x + 1
        return { value: curClazzNo, label: `${curClazzNo}班` }
      }),
    }
  }

  static buildClassOptionsByGradeClazzJoinInfo(
    joinInfo: IGradeClazzJoin,
    options: { disabledSummerName?: boolean } = {
      disabledSummerName: false,
    }
  ) {
    // 班级数量上限
    const clazzSize = joinInfo.class_limit > 0 ? joinInfo.class_limit : CLASS_PER_GRADE
    const result = {
      label: SchoolUtil.buildGradeName(joinInfo.grade_no, {
        disabledSummerName: options.disabledSummerName || false,
      }),
      value: joinInfo.grade_no,
      children: Array.from(Array(clazzSize).keys()).map((x) => {
        const curClazzNo = x + 1
        const noteName = joinInfo.custom_class_name_map[String(curClazzNo)]
        return { value: curClazzNo, label: noteName ? `${curClazzNo}班(${noteName})` : `${curClazzNo}班` }
      }),
    }
    return result
  }

  static async buildDefaultClassOptions() {
    return loginStore.isAuthed ? await this.buildDefaultClassOptionsWithNoteName() : this.buildDefaultClassOptionsOld()
  }

  static buildClassOptionsWithDisabled(classes: Clazz[], allGrades = false, allClasses = false) {
    const result = this.buildClassOptions(classes, allGrades, allClasses)

    const clazzDisabledMap = classes.reduce((result, clazz) => {
      result[clazz.id] = !clazz.available
      return result
    }, {} as { [index: number]: boolean })

    for (const grade of result) {
      let count = 0

      for (const clazz of grade.children || []) {
        clazz.disabled = clazzDisabledMap[clazz.value]

        if (clazz.disabled) {
          count++
        }
      }

      grade.disabled = count === (grade.children?.length || 0)
    }

    return result
  }

  static buildClassOptionsWithNoteName(clazzs: Clazz[], useGradeNo = false) {
    const clazzGroupByGrade = groupBy(clazzs, 'grade_no')
    return Object.keys(clazzGroupByGrade).map((gradeNo) => {
      return {
        label: SchoolUtil.buildGradeName(+gradeNo),
        value: useGradeNo ? +gradeNo : clazzs.find((item) => item.grade_no === +gradeNo)?.grade_id,
        children: clazzGroupByGrade[gradeNo as keyof typeof clazzGroupByGrade]
          .map((clazz) => {
            return {
              label: clazz.note_name || clazz.name || SchoolUtil.buildClazzName(+gradeNo, clazz.class_no),
              value: clazz.id,
              class_no: clazz.class_no,
            }
          })
          .sort((a, b) => a.class_no - b.class_no),
      }
    })
  }

  static buildClassOptionsWithCb(
    classes: Clazz[],
    gradeCb: OptionCbOfClazz = (clazz: Clazz) => ({
      value: clazz.grade_id,
      label: this.formatGrade(clazz.grade_no),
    }),
    clazzCb: OptionCbOfClazz = (clazz: Clazz) => ({
      label: clazz.note_name || clazz.name,
      value: clazz.id,
    })
  ) {
    const sorted = sortBy(classes, ['grade_no', 'class_no'])
    return sorted.reduce((grdOps: Option<any>[], clz: Clazz) => {
      const grdOp = gradeCb(clz)
      const clzOp = clazzCb(clz)
      const found = grdOps.find((grd) => grd.value === grdOp.value)
      if (found) {
        found.children!.push(clzOp)
      } else {
        grdOps.push({ ...grdOp, children: [clzOp] })
      }
      return grdOps
    }, [])
  }

  public static formatClazzNoteNameAndStudentAmounts(noteName: string | null, amount: number) {
    const studentsInfo = amount !== undefined ? `${amount}位学生` : ''
    const noteNameInfo = noteName ? `${noteName}  · ` : ''
    return noteName !== null ? `${noteNameInfo}${studentsInfo}` : `${studentsInfo}`
  }

  public static formatClazzNoteName(clazzInfo: Pick<Clazz, 'note_name' | 'name'>) {
    return clazzInfo.note_name !== null ? `${clazzInfo.note_name}` : `${clazzInfo.name}`
  }
}

interface IClazzDescribe {
  id: number | string
  count: number
  name: string
  avatar: string
  isMine: boolean
  note_name: string
  grade_no: number
  class_no: number
}

class ClazzDescribe implements IClazzDescribe {
  constructor(
    public id: number,
    public count: number,
    public name: string,
    public avatar: string,
    public isMine: boolean,
    public note_name: string,
    public grade_no: number,
    public class_no: number
  ) {}
}

class BaseClazzList<T extends IClazzDescribe> {
  public filter = '' // 根据名字filter
  // public static readonly FilterOption = 'all' // 仅 all的时候触发filter
  constructor(public items: T[]) {}

  get myItemsLength() {
    return this.showItems('mine').length
  }

  showItems(op: 'mine' | 'all' = 'all') {
    let items = this.items.filter((item) => op === 'all' || item.isMine)
    if (this.searchFilter) {
      items = items.filter(
        (x) =>
          this.makeFilterableContent(x.name).includes(this.searchFilter) ||
          this.makeFilterableContent(x.note_name).includes(this.searchFilter)
      )
    }
    return items
  }

  get searchFilter() {
    return this.makeFilterableContent(this.filter)
  }

  studentsAmount(op: 'mine' | 'all' = 'all') {
    return this.showItems(op).reduce((total, item) => total + item.count, 0)
  }

  clazzesAmount(op: 'mine' | 'all' = 'all') {
    return this.showItems(op).length
  }

  // 去掉中英文括号， 英文都变成大写
  makeFilterableContent(content: string) {
    if (!content) {
      content = ''
    }
    return content.replace('(', '').replace('（', '').replaceAll(')', '').replaceAll('）', '').toUpperCase()
  }
}

const avatars: { url: string; name: string }[] = [
  {
    url:
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2022-11-27/non_prod/8c153c63-7c60-4f24-a91a-bd0ed3927f7a/rope.png',
    name: '跳绳队',
  },
  {
    url:
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2022-11-27/non_prod/7eb44c31-f35d-468f-b081-f15c4ecaab0e/football.png',
    name: '足球队',
  },
  {
    url:
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2022-11-27/non_prod/973b0b30-cbcd-4ccc-9273-5db6cfa76d1e/basketball.png',
    name: '篮球队',
  },
  {
    url:
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2022-11-27/non_prod/83828175-8069-4625-bb68-bd854ecbd288/volleyball.png',
    name: '排球队',
  },
  {
    url:
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2022-11-27/non_prod/2d6b4b1a-2761-4d86-aaa4-cc5a442ace5d/cheers.png',
    name: '啦啦队',
  },
  {
    url:
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2022-11-30/non_prod/3edc551a-fde5-43a0-a373-9edbe735a681/flag.png',
    name: '升旗队',
  },
  {
    url:
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2023-01-29/non_prod/ba932ed7-6b22-4386-9588-ee6151884a4b/cc4a4be0-f02f-4fea-9573-0a4ae4a77773.png',
    name: '田径队',
  },
  {
    url:
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2022-11-27/non_prod/2028f41c-836f-4dd9-a7fe-b1d5918f3ddf/badminton.png',
    name: '羽毛球队',
  },
]

class SportsTeamCreateForm extends Form {
  static avatars = avatars

  name: string | null = null
  avatar: string | null = null

  @Register.registerRule('name')
  checkName() {
    if (!this.name) throw new Error('请填写运动会名称')
    return true
  }

  @Register.registerRule('avatar')
  checkAvatar() {
    if (!this.avatar) throw new Error('请选择运动会头像')
    return true
  }

  buildForm(): { note_name: string; img_url: string } {
    return {
      note_name: this.name!,
      img_url: this.avatar!,
    }
  }
}

class ClassServiceHelper {
  static createClazz(clz: Clazz, isCollege = false) {
    const studentsAmount = clz.student_amount
    const name = clz.note_name ? clz.note_name : clz.name
    const clzName = isCollege ? name + `(${ClassServiceHelper.achieveClazzCode(clz)})` : name
    const avartar =
      'https://cdn.tiantiantiaosheng.com/dataupload/tool/2022-11-27/non_prod/cda40639-a10d-4ec9-ba97-17f7b3dbb963/bc7a25aa-9de7-49ee-ab72-67c2e0144587.png'
    return new ClazzDescribe(
      clz.id,
      studentsAmount,
      clzName,
      avartar,
      clz.is_teaching,
      clz.note_name!,
      clz.grade_no,
      clz.class_no
    )
  }

  static createSportsTeam(clz: ISportsTeam) {
    const studentsAmount = clz.student_amount
    const name = clz.note_name ? clz.note_name : clz.name
    const clzName = name + ` (${ClassServiceHelper.achieveClazzCode(clz as any)})`
    const avartar = clz.img_url
    return new ClazzDescribe(
      clz.id,
      studentsAmount,
      clzName,
      avartar,
      clz.is_teaching,
      clz.note_name!,
      clz.grade_no,
      clz.class_no
    )
  }

  static createClazzList(clazzes: Clazz[], isCollege = false) {
    const clzs = clazzes.map((clz) => ClassServiceHelper.createClazz(clz, isCollege))
    return new BaseClazzList(clzs)
  }

  static createSportsTeamList(teams: ISportsTeam[]) {
    const ts = teams.map((team) => ClassServiceHelper.createSportsTeam(team))
    return new BaseClazzList(ts)
  }

  static achieveClazzCode(clazz: Clazz) {
    return `${clazz.grade_no > 70 || clazz.grade_no < 60 ? clazz.grade_no : clazz.grade_no - 20}${
      clazz.class_no < 100 ? (clazz.class_no < 10 ? '00' + clazz.class_no : '0' + clazz.class_no) : clazz.class_no
    }`
  }

  static createSportsTeamCreateForm() {
    return new SportsTeamCreateForm()
  }
}

export { BaseClazzList, ClazzDescribe, ClassServiceHelper, SportsTeamCreateForm }

export type { IClazzDescribe }
